import React from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import {
  Divider,
  Button,
  Message,
  Segment,
  Modal,
  Icon,
} from 'semantic-ui-react'

import { ProfilePicker } from 'components/profile-picker'
import { SettingsButton } from 'components/SettingsButton'
import { SettingsFooter } from 'components/SettingsFooter'

export const DRIVEPOINTS_BACKEND_URL =
  process.env.REACT_APP_DRIVEPOINTS_BACKEND_URL

import {
  updateProfile,
  doShowSettings,
  updatePermalink,
  resetSettings,
  sendMessage,
  zoomTo,
} from 'actions/commonActions'
import {
  uploadRouteFile,
  traceRoute,
  clearAnalyzeResults,
} from 'actions/analyzeActions'

import { withAuth } from 'utils/withAuth'
import { reverse_geocode } from 'utils/nominatim'

class AnalyzeControl extends React.Component {
  static propTypes = {
    profile: PropTypes.string.isRequired,
    dispatch: PropTypes.func.isRequired,
    loading: PropTypes.bool,
    auth: PropTypes.object,
    settings: PropTypes.object,
    analyze: PropTypes.object,
    parsedData: PropTypes.shape({
      start: PropTypes.shape({
        lat: PropTypes.number,
        lon: PropTypes.number,
        time: PropTypes.string,
      }),
      end: PropTypes.shape({
        lat: PropTypes.number,
        lon: PropTypes.number,
        time: PropTypes.string,
      }),
      points: PropTypes.arrayOf(
        PropTypes.shape({
          lat: PropTypes.number,
          lon: PropTypes.number,
          time: PropTypes.string,
        })
      ),
      duration: PropTypes.number,
    }),
  }

  constructor(props) {
    super(props)
    this.fileInputRef = React.createRef()
    this.state = {
      file: null,
      fileType: null,
      parsedData: null,
      errors: [],
      startAddress: null,
      endAddress: null,
      showFileInfoModal: false,
    }
  }

  componentDidUpdate(prevProps) {
    const { parsedData, profile, auth, settings, dispatch, loading } =
      this.props
    const prevProfile = prevProps.profile

    // Fetch addresses when parsedData changes
    if (parsedData && parsedData !== prevProps.parsedData) {
      this.fetchAddresses(parsedData.start, parsedData.end)
      // Zoom to start and end locations
      this.zoomToAnalyzeLocations(parsedData.start, parsedData.end)

      // Automatically run analyze when file is loaded (only if not already loading)
      if (
        parsedData &&
        parsedData.points &&
        parsedData.points.length > 0 &&
        !loading
      ) {
        dispatch(traceRoute({ parsedData, auth, profile, settings }))
      }
    }

    // Re-run analyze when profile changes between autodp and truckdp
    if (
      parsedData &&
      parsedData.points &&
      parsedData.points.length > 0 &&
      prevProfile !== profile &&
      (prevProfile === 'autodp' || prevProfile === 'truckdp') &&
      (profile === 'autodp' || profile === 'truckdp') &&
      !loading
    ) {
      dispatch(traceRoute({ parsedData, auth, profile, settings }))
    }
  }

  handleUpdateProfile = (event, data) => {
    const { dispatch } = this.props
    dispatch(updateProfile({ profile: data.valhalla_profile }))
    dispatch(resetSettings())
    dispatch(updatePermalink())
  }

  handleSettings = () => {
    const { dispatch } = this.props
    dispatch(doShowSettings())
  }

  handleToggleFileInfoModal = () => {
    this.setState((prevState) => ({
      showFileInfoModal: !prevState.showFileInfoModal,
    }))
  }

  formatShortAddress = (addressObj) => {
    if (!addressObj) {
      return null
    }

    const parts = []
    if (addressObj.house_number) {
      parts.push(addressObj.house_number)
    }
    if (addressObj.road) {
      parts.push(addressObj.road)
    }
    if (addressObj.city || addressObj.town || addressObj.village) {
      parts.push(addressObj.city || addressObj.town || addressObj.village)
    }
    if (addressObj.state || addressObj.state_district) {
      parts.push(addressObj.state || addressObj.state_district)
    }
    if (addressObj.postcode) {
      parts.push(addressObj.postcode)
    }

    return parts.length > 0 ? parts.join(' ') : null
  }

  fetchAddresses = (start, end) => {
    // Fetch start address
    reverse_geocode(start.lon, start.lat)
      .then((response) => {
        const addressObj = response.data?.address
        const shortAddress = this.formatShortAddress(addressObj)
        this.setState({ startAddress: shortAddress })
      })
      .catch((error) => {
        console.error('Error fetching start address:', error)
        this.setState({ startAddress: null })
      })

    // Fetch end address
    reverse_geocode(end.lon, end.lat)
      .then((response) => {
        const addressObj = response.data?.address
        const shortAddress = this.formatShortAddress(addressObj)
        this.setState({ endAddress: shortAddress })
      })
      .catch((error) => {
        console.error('Error fetching end address:', error)
        this.setState({ endAddress: null })
      })
  }

  zoomToAnalyzeLocations = (start, end) => {
    const { dispatch, parsedData } = this.props
    // Use all points from parsedData for bounding box, not just start and end
    let coordinates
    if (parsedData && parsedData.points && parsedData.points.length > 0) {
      coordinates = parsedData.points.map((point) => [point.lat, point.lon])
    } else {
      // Fallback to start and end if points array is not available
      coordinates = [
        [start.lat, start.lon],
        [end.lat, end.lon],
      ]
    }
    dispatch(zoomTo(coordinates))
  }

  handleFileChange = (event) => {
    const file = event.target.files[0]
    if (!file) {
      return
    }

    const fileName = file.name.toLowerCase()
    let fileType = null

    if (fileName.endsWith('.gpx')) {
      fileType = 'gpx'
    } else if (fileName.endsWith('.csv')) {
      fileType = 'csv'
    } else {
      this.props.dispatch(
        sendMessage({
          type: 'error',
          icon: 'error',
          description: 'Please upload a GPX or CSV file',
          title: 'Invalid File Type',
        })
      )
      return
    }

    this.setState({
      file,
      fileType,
      parsedData: null,
      errors: [],
      startAddress: null,
      endAddress: null,
    })

    // Clear all analyze results and map layers when new file is selected
    this.props.dispatch(clearAnalyzeResults())

    // Parse the file
    const reader = new FileReader()
    reader.onload = (e) => {
      const content = e.target.result
      this.parseFile(content, fileType, file.name)
    }
    reader.onerror = () => {
      this.props.dispatch(
        sendMessage({
          type: 'error',
          icon: 'error',
          description: 'Failed to read file',
          title: 'File Read Error',
        })
      )
    }

    if (fileType === 'gpx') {
      reader.readAsText(file)
    } else {
      reader.readAsText(file)
    }
  }

  parseFile = (content, fileType, fileName) => {
    if (fileType === 'gpx') {
      this.parseGPX(content)
    } else if (fileType === 'csv') {
      this.parseCSV(content)
    }
  }

  parseGPX = (content) => {
    try {
      const parser = new DOMParser()
      const xmlDoc = parser.parseFromString(content, 'text/xml')

      // Check for parsing errors
      const parserError = xmlDoc.querySelector('parsererror')
      if (parserError) {
        throw new Error('Invalid GPX XML format')
      }

      // Find all trkpt or wpt elements
      const trackPoints = xmlDoc.querySelectorAll('trkpt, wpt')
      if (trackPoints.length === 0) {
        throw new Error('No track points or waypoints found in GPX file')
      }

      const points = []
      trackPoints.forEach((pt) => {
        const lat = parseFloat(pt.getAttribute('lat'))
        const lon = parseFloat(pt.getAttribute('lon'))
        const timeElement = pt.querySelector('time')
        const time = timeElement ? timeElement.textContent : null
        const eleElement = pt.querySelector('ele')
        const elevation = eleElement ? parseFloat(eleElement.textContent) : null

        if (!isNaN(lat) && !isNaN(lon)) {
          points.push({ lat, lon, time, elevation })
        }
      })

      if (points.length === 0) {
        throw new Error('No valid coordinates found in GPX file')
      }

      const start = points[0]
      const end = points[points.length - 1]

      // Validate coordinates
      const errors = []
      if (
        start.lat < -90 ||
        start.lat > 90 ||
        start.lon < -180 ||
        start.lon > 180
      ) {
        errors.push('Start location coordinates are out of valid range')
      }
      if (end.lat < -90 || end.lat > 90 || end.lon < -180 || end.lon > 180) {
        errors.push('End location coordinates are out of valid range')
      }

      if (errors.length > 0) {
        this.props.dispatch(
          sendMessage({
            type: 'error',
            icon: 'error',
            description: errors.join('; '),
            title: 'Validation Error',
          })
        )
        return
      }

      // Calculate duration if times are available
      let duration = null
      if (start.time && end.time) {
        const startTime = new Date(start.time)
        const endTime = new Date(end.time)
        if (!isNaN(startTime.getTime()) && !isNaN(endTime.getTime())) {
          duration = endTime - startTime
        }
      }

      const parsedData = {
        start: { lat: start.lat, lon: start.lon, time: start.time },
        end: { lat: end.lat, lon: end.lon, time: end.time },
        points,
        duration,
        type: 'gpx',
      }

      this.setState({ parsedData, errors: [] })
      this.props.dispatch(uploadRouteFile(parsedData))
    } catch (error) {
      this.props.dispatch(
        sendMessage({
          type: 'error',
          icon: 'error',
          description: error.message || 'Failed to parse GPX file',
          title: 'GPX Parse Error',
        })
      )
    }
  }

  parseCSV = (content) => {
    try {
      const lines = content.split('\n').filter((line) => line.trim())
      if (lines.length < 2) {
        throw new Error(
          'CSV file must have at least a header row and one data row'
        )
      }

      // Simple CSV parser that handles quoted values
      const parseCSVLine = (line) => {
        const result = []
        let current = ''
        let inQuotes = false

        for (let i = 0; i < line.length; i++) {
          const char = line[i]
          if (char === '"') {
            inQuotes = !inQuotes
          } else if (char === ',' && !inQuotes) {
            result.push(current.trim())
            current = ''
          } else {
            current += char
          }
        }
        result.push(current.trim())
        return result
      }

      // Parse header
      const headerLine = lines[0]
      const headers = parseCSVLine(headerLine).map((header) =>
        header.replace(/^"|"$/g, '').trim().toLowerCase()
      )

      // Find lat/lon columns
      let latIndex = -1
      let lonIndex = -1
      let timeIndex = -1

      headers.forEach((header, index) => {
        const normalized = header.toLowerCase()
        if (
          normalized === 'lat' ||
          normalized === 'latitude' ||
          normalized.startsWith('lat')
        ) {
          latIndex = index
        }
        if (
          normalized === 'lon' ||
          normalized === 'long' ||
          normalized === 'longitude' ||
          normalized.startsWith('lon') ||
          normalized.startsWith('long')
        ) {
          lonIndex = index
        }
        if (
          normalized === 'time' ||
          normalized === 'datetime' ||
          normalized === 'date_time' ||
          normalized === 'timestamp'
        ) {
          timeIndex = index
        }
      })

      if (latIndex === -1 || lonIndex === -1) {
        throw new Error(
          'Could not find latitude and longitude columns. Please ensure your CSV has columns named "lat"/"latitude" and "lon"/"long"/"longitude"'
        )
      }

      // Parse data rows
      const points = []
      const errors = []

      for (let i = 1; i < lines.length; i++) {
        const values = parseCSVLine(lines[i]).map((v) =>
          v.replace(/^"|"$/g, '').trim()
        )
        const lat = parseFloat(values[latIndex])
        const lon = parseFloat(values[lonIndex])
        const time = timeIndex >= 0 ? values[timeIndex] : null

        // Validate coordinates
        if (isNaN(lat) || isNaN(lon)) {
          errors.push(`Row ${i + 1}: Invalid latitude or longitude values`)
          continue
        }

        if (lat < -90 || lat > 90) {
          errors.push(
            `Row ${i + 1}: Latitude ${lat} is out of valid range (-90 to 90)`
          )
          continue
        }

        if (lon < -180 || lon > 180) {
          errors.push(
            `Row ${i + 1}: Longitude ${lon} is out of valid range (-180 to 180)`
          )
          continue
        }

        points.push({ lat, lon, time })
      }

      if (points.length === 0) {
        throw new Error('No valid data points found in CSV file')
      }

      if (errors.length > 0) {
        this.props.dispatch(
          sendMessage({
            type: 'error',
            icon: 'error',
            description: errors.join('; '),
            title: 'CSV Validation Errors',
          })
        )
        // Continue with valid points if any exist
        if (points.length === 0) {
          return
        }
      }

      const start = points[0]
      const end = points[points.length - 1]

      // Calculate duration if times are available
      let duration = null
      if (start.time && end.time) {
        const startTime = new Date(start.time)
        const endTime = new Date(end.time)
        if (!isNaN(startTime.getTime()) && !isNaN(endTime.getTime())) {
          duration = endTime - startTime
        }
      }

      const parsedData = {
        start: { lat: start.lat, lon: start.lon, time: start.time },
        end: { lat: end.lat, lon: end.lon, time: end.time },
        points,
        duration,
        type: 'csv',
      }

      this.setState({ parsedData, errors })
      this.props.dispatch(uploadRouteFile(parsedData))
    } catch (error) {
      this.props.dispatch(
        sendMessage({
          type: 'error',
          icon: 'error',
          description: error.message || 'Failed to parse CSV file',
          title: 'CSV Parse Error',
        })
      )
    }
  }

  handleAnalyze = () => {
    const { dispatch, auth, profile, settings } = this.props
    const { parsedData } = this.state

    if (!parsedData) {
      dispatch(
        sendMessage({
          type: 'warning',
          icon: 'warning',
          description: 'Please upload and parse a route file first',
          title: 'No Route Data',
        })
      )
      return
    }

    // First trace the route
    dispatch(traceRoute({ parsedData, auth, profile, settings }))
  }

  getProfiles = () => {
    // Analyze tab should only have drivepoints profiles
    if (DRIVEPOINTS_BACKEND_URL) {
      return ['autodp', 'truckdp']
    }
    return []
  }

  getPopupContent = () => {
    // Analyze tab should only have drivepoints profiles
    if (DRIVEPOINTS_BACKEND_URL) {
      return ['Drivepoints Car', 'Drivepoints Truck']
    }
    return []
  }

  formatDuration = (ms) => {
    if (!ms) {
      return null
    }
    const seconds = Math.floor(ms / 1000)
    const minutes = Math.floor(seconds / 60)
    const hours = Math.floor(minutes / 60)
    const days = Math.floor(hours / 24)

    if (days > 0) {
      return `${days}d ${hours % 24}h ${minutes % 60}m`
    }
    if (hours > 0) {
      return `${hours}h ${minutes % 60}m ${seconds % 60}s`
    }
    if (minutes > 0) {
      return `${minutes}m ${seconds % 60}s`
    }
    return `${seconds}s`
  }

  render() {
    const { profile, loading, parsedData } = this.props
    const { file, fileType, startAddress, endAddress } = this.state

    return (
      <React.Fragment>
        <div
          className="flex flex-column content-between"
          style={{ maxWidth: '100%', overflow: 'hidden' }}
        >
          <div>
            <div className="pa2 flex flex-row justify-between">
              <ProfilePicker
                group={'analyze'}
                profiles={this.getProfiles()}
                loading={loading}
                popupContent={this.getPopupContent()}
                activeProfile={profile}
                handleUpdateProfile={this.handleUpdateProfile}
              />
              <div className="flex flex-row">
                <SettingsButton handleSettings={this.handleSettings} />
              </div>
            </div>

            <div className="pa2">
              <Segment
                style={{
                  maxWidth: '100%',
                  overflow: 'hidden',
                  wordWrap: 'break-word',
                }}
              >
                {!parsedData && (
                  <Message info>
                    <Message.Header>
                      We are working on this capability
                    </Message.Header>
                    <p>Upload route data to get started with route analysis.</p>
                  </Message>
                )}
                <h3
                  style={{
                    wordBreak: 'break-word',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}
                >
                  Upload Route Data
                  <Icon
                    name="info circle"
                    style={{ cursor: 'pointer', color: '#4183c4' }}
                    onClick={this.handleToggleFileInfoModal}
                  />
                </h3>
                <p style={{ wordBreak: 'break-word' }}>
                  Upload a GPX file or CSV file containing route coordinates.{' '}
                  <br />
                  <i>
                    Note: we only support routing alternatives for A - B trips.
                    No multi-stop trips.
                  </i>
                </p>

                <div
                  style={{
                    marginTop: '15px',
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    gap: '10px',
                  }}
                >
                  <input
                    type="file"
                    accept=".gpx,.csv"
                    onChange={this.handleFileChange}
                    ref={this.fileInputRef}
                    style={{ display: 'none' }}
                  />
                  <Button
                    onClick={() => {
                      if (this.fileInputRef.current) {
                        this.fileInputRef.current.click()
                      }
                    }}
                    primary
                  >
                    Choose File
                  </Button>
                  {file && (
                    <span style={{ wordBreak: 'break-word', maxWidth: '100%' }}>
                      Selected: {file.name} ({fileType?.toUpperCase()})
                    </span>
                  )}
                </div>

                {parsedData && (
                  <div
                    style={{
                      marginTop: '20px',
                      maxWidth: '100%',
                      overflow: 'hidden',
                    }}
                  >
                    <Message success>
                      <Message.Header>File Parsed Successfully</Message.Header>
                      <p
                        style={{
                          wordBreak: 'break-word',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                        }}
                      >
                        <span
                          style={{
                            display: 'inline-block',
                            width: '16px',
                            height: '16px',
                            borderRadius: '50%',
                            backgroundColor: '#28a745',
                            flexShrink: 0,
                          }}
                        />
                        <span>
                          <strong>Start:</strong>{' '}
                          {parsedData.start.lat.toFixed(6)},{' '}
                          {parsedData.start.lon.toFixed(6)}
                          {startAddress && (
                            <span>
                              <br />
                              <em>{startAddress}</em>
                            </span>
                          )}
                        </span>
                      </p>
                      <p
                        style={{
                          wordBreak: 'break-word',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                        }}
                      >
                        <span
                          style={{
                            display: 'inline-block',
                            width: '16px',
                            height: '16px',
                            borderRadius: '50%',
                            backgroundColor: '#dc3545',
                            flexShrink: 0,
                          }}
                        />
                        <span>
                          <strong>End:</strong> {parsedData.end.lat.toFixed(6)},{' '}
                          {parsedData.end.lon.toFixed(6)}
                          {endAddress && (
                            <span>
                              <br />
                              <em>{endAddress}</em>
                            </span>
                          )}
                        </span>
                      </p>
                      {parsedData.start.time && (
                        <p style={{ wordBreak: 'break-word' }}>
                          <strong>Start Time:</strong>{' '}
                          {new Date(parsedData.start.time).toLocaleString()}
                        </p>
                      )}
                      {parsedData.end.time && (
                        <p style={{ wordBreak: 'break-word' }}>
                          <strong>End Time:</strong>{' '}
                          {new Date(parsedData.end.time).toLocaleString()}
                        </p>
                      )}
                      {parsedData.duration && (
                        <p style={{ wordBreak: 'break-word' }}>
                          <strong>Duration:</strong>{' '}
                          {this.formatDuration(parsedData.duration)}
                        </p>
                      )}
                    </Message>
                  </div>
                )}
              </Segment>
            </div>
          </div>
          <Divider fitted />
          <SettingsFooter />
        </div>
        <Modal
          open={this.state.showFileInfoModal}
          onClose={this.handleToggleFileInfoModal}
          size="small"
        >
          <Modal.Header>File Format Information</Modal.Header>
          <Modal.Content>
            <Modal.Description>
              <p style={{ wordBreak: 'break-word', marginBottom: '10px' }}>
                Upload a GPX file or CSV file containing route coordinates.
              </p>
              <ul
                style={{
                  marginLeft: '20px',
                  marginTop: '10px',
                  wordBreak: 'break-word',
                }}
              >
                <li style={{ marginBottom: '10px' }}>
                  <strong>GPX files:</strong> Should contain track points
                  (trkpt) or waypoints (wpt) with latitude and longitude
                  coordinates
                </li>
                <li>
                  <strong>CSV files:</strong> Should have columns for latitude
                  (lat, latitude) and longitude (lon, long, longitude).
                  Optional: time, datetime, or timestamp column for date/time
                  information
                </li>
              </ul>
            </Modal.Description>
          </Modal.Content>
          <Modal.Actions>
            <Button onClick={this.handleToggleFileInfoModal}>OK</Button>
          </Modal.Actions>
        </Modal>
      </React.Fragment>
    )
  }
}

const mapStateToProps = (state) => {
  const { profile, loading, settings } = state.common
  const { analyze } = state
  return {
    profile,
    loading,
    settings,
    analyze,
    parsedData: analyze?.parsedData,
  }
}

export default connect(mapStateToProps)(withAuth(AnalyzeControl))
